import { cachedFetch } from './signedAssetCache';
import { buildBlueprint } from '../Mapping/Arrange/logic';

const FACE = 'Face';
const PERSON = 'Person';

/** CSS layout size per tile (16:9). Map scales the sheet — never squash via width %. */
const TILE_CSS_W = 160;
const TILE_CSS_H = Math.round((TILE_CSS_W * 9) / 16);

const sortSessionRegions = (regions = []) => (
  [...regions].sort((a, b) => {
    const cam = String(a.camera || '').localeCompare(String(b.camera || ''));
    return cam || String(a.regionId || '').localeCompare(String(b.regionId || ''));
  })
);

/**
 * Stadium = same blueprint as Arrange (exact sizes + poses).
 * Tiles paint inside those rectangles; Map fit=contain scales the sheet.
 */
const stadiumContentLayout = (entries = []) => {
  const list = entries || [];
  if (!list.length) {
    return { stageW: TILE_CSS_W, stageH: TILE_CSS_H, slots: [] };
  }

  const regions = list.map((entry, i) => ({
    id: entry.scanId || `slot-${i}`,
    camIdx: entry.camera,
    regionId: entry.regionId,
    label: entry.label,
    tileWidth: entry.tileWidth,
    tileHeight: entry.tileHeight,
    rows: entry.rows,
    cols: entry.cols,
    arrangeLayout: entry.arrangeLayout,
  }));

  const { slots, stageW, stageH } = buildBlueprint(regions);
  if (!slots.length) {
    return { stageW: TILE_CSS_W, stageH: TILE_CSS_H, slots: [] };
  }

  // Re-attach scan payload onto blueprint slots (matched by id).
  const byId = new Map(list.map((e, i) => [e.scanId || `slot-${i}`, e]));
  return {
    stageW: Math.max(1, Math.ceil(stageW)),
    stageH: Math.max(1, Math.ceil(stageH)),
    slots: slots.map((slot) => {
      const entry = byId.get(slot.id) || {};
      return {
        ...entry,
        width: slot.width,
        height: slot.height,
        angle: slot.angle,
        left: slot.left,
        top: slot.top,
      };
    }),
  };
};

const BOOL_FACE_ATTRS = [
  'Smile',
  'Eyeglasses',
  'Sunglasses',
  'Beard',
  'Mustache',
  'EyesOpen',
  'MouthOpen',
  'FaceOccluded',
];

const BOX_COLORS = ['#f5c542', '#4db6ff', '#81c784', '#ff8a65', '#ce93d8', '#4dd0e1', '#ffd54f', '#ef9a9a'];

const SELECT_STYLES = {
  control: (base) => ({
    ...base,
    minHeight: 38,
    height: 38,
    borderRadius: 30,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 110,
    borderRadius: 30,
    overflow: 'hidden',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  valueContainer: (base) => ({
    ...base,
    height: 38,
    padding: '0 8px 0 14px',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 38,
  }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    lineHeight: '20px',
  }),
  placeholder: (base) => ({
    ...base,
    margin: 0,
    lineHeight: '20px',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
};

const DETECTABLES_SELECT_STYLES = {
  ...SELECT_STYLES,
  control: (base) => ({
    ...SELECT_STYLES.control(base),
    minWidth: 180,
  }),
  valueContainer: (base) => ({
    ...SELECT_STYLES.valueContainer(base),
    position: 'relative',
    flexWrap: 'nowrap',
  }),
  option: (base, state) => {
    let backgroundColor = 'transparent';
    if (state.isSelected) backgroundColor = '#d9e8ff';
    else if (state.isFocused) backgroundColor = '#f0f0f0';
    return {
      ...base,
      backgroundColor,
      color: '#333',
      cursor: 'pointer',
    };
  },
};

/** Session = header; region rows indented under it. */
const SCAN_SELECT_STYLES = {
  ...SELECT_STYLES,
  option: (base, state) => {
    let backgroundColor = 'transparent';
    if (state.isSelected) backgroundColor = '#d9e8ff';
    else if (state.isFocused) backgroundColor = '#f0f0f0';
    const isRegion = state.data?.kind === 'region';
    return {
      ...base,
      backgroundColor,
      color: '#333',
      cursor: 'pointer',
      fontWeight: isRegion ? 400 : 600,
      paddingLeft: isRegion ? 36 : 12,
    };
  },
};

const colorForLabel = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.codePointAt(i)) % BOX_COLORS.length;
  }
  return BOX_COLORS[Math.abs(hash) % BOX_COLORS.length];
};

const fmtPct = (n) => (Number.isFinite(n) ? `${n.toFixed(1)}%` : null);

const fmtPtz = (n) => (Number.isFinite(n) ? String(Math.round(n * 100) / 100) : '');

const formatSession = (session) => {
  const ms = Number(session);
  if (!Number.isFinite(ms)) return String(session);
  const d = new Date(ms);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

/**
 * Flat scan options — session row is selectable; region rows sit under it.
 * (No react-select group heading, which duplicated the session label.)
 */
const buildScanOptions = (sessions, { includeRegions = false } = {}) => (
  (sessions || []).flatMap((s) => {
    const regions = sortSessionRegions(s.regions || []);
    if (!regions.length) return [];

    const session = String(s.session);
    const sessionOption = {
      value: `session:${session}`,
      label: formatSession(session),
      kind: 'session',
      session,
      regionIds: regions.map((r) => String(r._id)),
    };

    if (!includeRegions) return [sessionOption];

    return [
      sessionOption,
      ...regions.map((r) => ({
        value: `region:${r._id}`,
        label: `${r.camera} - ${r.regionId}`,
        kind: 'region',
        id: String(r._id),
        session,
        rows: r.rows,
        cols: r.cols,
        camera: r.camera,
        regionId: r.regionId,
      })),
    ];
  })
);

/** Flatten grouped or flat scan option lists. */
const flatScanOptions = (items) => (items || []).flatMap((item) => (
  Array.isArray(item?.options) ? item.options : [item]
));

/** Prefer first full-scan (session) option, else first option. */
const defaultScanOption = (options) => {
  const flat = flatScanOptions(options);
  return flat.find((o) => o.kind === 'session') || flat[0] || null;
};

const scanIdsFromOption = (opt) => {
  if (!opt) return [];
  return opt.kind === 'session' ? (opt.regionIds || []) : [opt.id];
};

const tileKey = (tile) => `${tile.row}-${tile.col}-${tile.index}`;

/**
 * Tile grid coordinates:
 * - Stored / keyed / drawn: 0-based (API `tile.row`, `tile.col`, `tile.index`)
 * - Shown to humans: 1-based (r1·c1 is the top-left cell)
 */
const tileRowLabel = (row) => (Number(row) || 0) + 1;
const tileColLabel = (col) => (Number(col) || 0) + 1;
const tileIndexLabel = (index) => (Number(index) || 0) + 1;
const fmtTileRC = (row, col) => `r${tileRowLabel(row)} · c${tileColLabel(col)}`;
const fmtTilePos = (tile) => (
  `${fmtTileRC(tile?.row, tile?.col)} · #${tileIndexLabel(tile?.index)}`
);

/** Lookup key for tileBoxes map (prefixes with scan id in stadium mode). */
const tileBoxesKey = (tile, scanId = tile?._scanId) => (
  scanId ? `${scanId}:${tileKey(tile)}` : tileKey(tile)
);

const sortTiles = (tiles) => [...(tiles || [])].sort((a, b) => a.index - b.index);

/** Absolute pan/tilt for a mosaic cell. Prefers scanBounds steps (same as SRS);
 * falls back to endpoint lerp for legacy scans without panStep/tiltStep. */
const scanCellPtz = (scan, col, row) => {
  const b = scan?.scanBounds || {};
  const start = b.start || {};
  const panStep = Number(b.panStep ?? scan?.panStep);
  const tiltStep = Number(b.tiltStep ?? scan?.tiltStep);
  const c = Number(col) || 0;
  const r = Number(row) || 0;
  const zoom = Number.isFinite(Number(b.zoom)) ? Number(b.zoom) : null;

  if (Number.isFinite(start.pan) && Number.isFinite(panStep)
    && Number.isFinite(start.tilt) && Number.isFinite(tiltStep)) {
    return {
      pan: start.pan + c * panStep,
      tilt: start.tilt + r * tiltStep,
      zoom,
    };
  }

  const end = b.end || {};
  const rows = scan?.rows || 1;
  const cols = scan?.cols || 1;
  const colT = cols <= 1 ? 0 : c / (cols - 1);
  const rowT = rows <= 1 ? 0 : r / (rows - 1);
  const lerp = (a, b2, t) => (Number.isFinite(a) && Number.isFinite(b2) ? a + (b2 - a) * t : null);
  return {
    pan: lerp(start.pan, end.pan, colT),
    tilt: lerp(start.tilt, end.tilt, rowT),
    zoom,
  };
};

const tilePtz = (tile, scan) => scanCellPtz(scan, tile?.col, tile?.row);

const boolVal = (attr) => (attr && typeof attr.Value === 'boolean' ? attr.Value : null);

const toCamel = (name) => name.charAt(0).toLowerCase() + name.slice(1);

const faceDetails = (face) => {
  const rows = [];
  const conf = fmtPct(face.Confidence);
  if (conf) rows.push(['Confidence', conf]);
  if (Number.isFinite(face.AgeRange?.Low) && Number.isFinite(face.AgeRange?.High)) {
    rows.push(['Age', `${face.AgeRange.Low} – ${face.AgeRange.High}`]);
  }
  if (face.Gender?.Value) {
    const g = fmtPct(face.Gender.Confidence);
    rows.push(['Gender', g ? `${face.Gender.Value} (${g})` : face.Gender.Value]);
  }
  BOOL_FACE_ATTRS.forEach((key) => {
    const val = face[key];
    if (!val || typeof val !== 'object' || !('Value' in val)) return;
    const c = fmtPct(val.Confidence);
    rows.push([key, c ? `${val.Value} (${c})` : String(val.Value)]);
  });
  if (face.Emotions?.length) {
    const top = [...face.Emotions]
      .filter((e) => e?.Type)
      .sort((a, b) => (b.Confidence || 0) - (a.Confidence || 0))
      .slice(0, 3)
      .map((e) => `${e.Type} ${fmtPct(e.Confidence) || ''}`.trim())
      .join(', ');
    if (top) rows.push(['Emotions', top]);
  }
  return rows;
};

const faceAttrFields = (face) => {
  const out = {};
  BOOL_FACE_ATTRS.forEach((key) => {
    out[toCamel(key)] = boolVal(face[key]);
  });
  return out;
};

const fetchJson = async (url) => {
  if (!url) return null;
  try {
    const res = await cachedFetch(url);
    return res?.ok ? res.json() : null;
  } catch {
    return null;
  }
};

/** Run async work over items with a max concurrency (avoids browser resource errors). */
const mapPool = async (items, concurrency, worker) => {
  const list = items || [];
  if (!list.length) return [];
  const limit = Math.max(1, Math.min(concurrency || 1, list.length));
  const results = new Array(list.length);
  let next = 0;

  const run = async () => {
    while (next < list.length) {
      const i = next;
      next += 1;
      // Sequential slot work within each pool worker.
      // eslint-disable-next-line no-await-in-loop
      results[i] = await worker(list[i], i);
    }
  };

  await Promise.all(Array.from({ length: limit }, () => run()));
  return results;
};

/** Parse DetectFaces + DetectLabels into overlay boxes + scene labels. */
const parseRekognition = (key, faces, labelsPayload) => {
  const boxes = [];
  (faces?.FaceDetails || []).forEach((face, i) => {
    if (!face?.BoundingBox) return;
    const emotions = [...(face.Emotions || [])]
      .filter((e) => e?.Type)
      .sort((a, b) => (b.Confidence || 0) - (a.Confidence || 0));
    boxes.push({
      id: `${key}-face-${i}`,
      label: FACE,
      confidence: Number(face.Confidence) || 0,
      ageLow: Number.isFinite(face.AgeRange?.Low) ? face.AgeRange.Low : null,
      ageHigh: Number.isFinite(face.AgeRange?.High) ? face.AgeRange.High : null,
      gender: face.Gender?.Value || null,
      genderConfidence: Number(face.Gender?.Confidence) || null,
      ...faceAttrFields(face),
      topEmotion: emotions[0]?.Type || null,
      topEmotionConfidence: Number(emotions[0]?.Confidence) || null,
      BoundingBox: face.BoundingBox,
      details: faceDetails(face),
      raw: face,
    });
  });

  const labels = [];
  (labelsPayload?.Labels || []).forEach((label) => {
    if (!label?.Name) return;
    const instances = (label.Instances || []).filter((inst) => inst?.BoundingBox);
    const parents = (label.Parents || []).map((p) => p.Name).filter(Boolean);
    const aliases = (label.Aliases || []).map((a) => a.Name).filter(Boolean);
    const categories = (label.Categories || []).map((c) => c.Name).filter(Boolean);
    labels.push({
      name: label.Name,
      confidence: Number(label.Confidence) || 0,
      parents,
      aliases,
      categories,
      instanceCount: instances.length,
      tileKey: key,
      raw: label,
    });
    instances.forEach((inst, i) => {
      const confidence = Number(inst.Confidence ?? label.Confidence) || 0;
      const details = [
        ['Confidence', fmtPct(confidence)],
        parents.length ? ['Parents', parents.join(', ')] : null,
        aliases.length ? ['Aliases', aliases.join(', ')] : null,
        categories.length ? ['Categories', categories.join(', ')] : null,
        Number.isFinite(label.Confidence) ? ['Label confidence', fmtPct(label.Confidence)] : null,
      ].filter(Boolean);
      boxes.push({
        id: `${key}-${label.Name}-${i}`,
        label: label.Name,
        confidence,
        parents,
        aliases,
        categories,
        BoundingBox: inst.BoundingBox,
        details,
        raw: {
          Name: label.Name,
          Confidence: label.Confidence,
          Parents: label.Parents || [],
          Aliases: label.Aliases || [],
          Categories: label.Categories || [],
          Instance: inst,
        },
      });
    });
  });

  return { boxes, labels };
};

const countableDetectables = (tileBoxes) => {
  const counts = new Map();
  Object.values(tileBoxes).forEach((boxes) => {
    boxes.forEach((b) => {
      if (!b?.label) return;
      counts.set(b.label, (counts.get(b.label) || 0) + 1);
    });
  });
  return Array.from(counts.entries())
    .sort(([aName, aCount], [bName, bCount]) => {
      const aPinned = aName === FACE || aName === PERSON;
      const bPinned = bName === FACE || bName === PERSON;
      if (aPinned !== bPinned) return aPinned ? -1 : 1;
      return bCount - aCount || aName.localeCompare(bName);
    })
    .map(([name, count]) => ({ value: name, label: `${name} (${count})` }));
};

export {
  FACE,
  PERSON,
  BOOL_FACE_ATTRS,
  SELECT_STYLES,
  SCAN_SELECT_STYLES,
  DETECTABLES_SELECT_STYLES,
  colorForLabel,
  fmtPtz,
  formatSession,
  buildScanOptions,
  flatScanOptions,
  defaultScanOption,
  scanIdsFromOption,
  tileKey,
  tileBoxesKey,
  tileRowLabel,
  tileColLabel,
  tileIndexLabel,
  fmtTileRC,
  fmtTilePos,
  sortTiles,
  scanCellPtz,
  tilePtz,
  fetchJson,
  mapPool,
  parseRekognition,
  countableDetectables,
  sortSessionRegions,
  TILE_CSS_W,
  TILE_CSS_H,
  stadiumContentLayout,
};
